import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Accordion, AccordionSummary, AccordionDetails,
  FormControl, InputLabel, Select, MenuItem, Radio, RadioGroup, FormControlLabel,
  FormLabel, Checkbox, Snackbar, Alert, CircularProgress, Paper, Container
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircleOutline as CheckCircleIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import { asArray, parseBookingSearch } from '../utils/showings';

// Privacy Policy Text Template
const getPrivacyPolicyText = (managerName, jurisdiction) => {
  const landlordName = managerName || 'The LANDLORD';
  
  let privacyActText = 'applicable privacy laws';
  if (jurisdiction) {
    const jurisdictionUpper = jurisdiction.toUpperCase();
    if (jurisdictionUpper.includes('ALBERTA') || jurisdictionUpper.includes('AB')) {
      privacyActText = "Alberta's Personal Information Protection Act";
    } else if (jurisdictionUpper.includes('BRITISH COLUMBIA') || jurisdictionUpper.includes('BC')) {
      privacyActText = "British Columbia's Personal Information Protection Act";
    } else if (jurisdictionUpper.includes('ONTARIO') || jurisdictionUpper.includes('ON')) {
      privacyActText = "Ontario's Personal Information Protection Act";
    } else if (jurisdictionUpper.includes('QUEBEC') || jurisdictionUpper.includes('QC')) {
      privacyActText = "Quebec's Act Respecting the Protection of Personal Information in the Private Sector";
    } else if (jurisdictionUpper.includes('CALIFORNIA') || jurisdictionUpper.includes('CA')) {
      privacyActText = "California's Consumer Privacy Act (CCPA) and other applicable laws";
    } else if (jurisdictionUpper.includes('NEW YORK') || jurisdictionUpper.includes('NY')) {
      privacyActText = "New York's privacy laws and other applicable laws";
    } else {
      privacyActText = `${jurisdiction}'s privacy laws and other applicable laws`;
    }
  }
  
  return `${landlordName} ['The LANDLORD'] is committed to safeguarding the personal information entrusted to us by the Applicant. Personal information means any information about an identifiable individual. This can include but is not limited to an individual's name, home address and phone number, age, sex, marital or family status, financial information, educational history, or employment status.

We manage your personal information in accordance with ${privacyActText}. This policy applies to the LANDLORD and to any person providing services on our behalf.

We collect the personal information for the purposes of assessing the Applicant(s) and co-Applicant[s] if any as to suitability as a tenant in general, and/or for a specific rental location.

Your completion of this Application will be taken as your consent to the above use by us, including both the information provided in the Application and personal information obtained by us from other sources as noted in the Application. We will assume the same consent in cases where you volunteer other personal information for an obvious purpose, even though it may not be specifically requested in the Application.

You may withdraw consent to the use and disclosure of personal information at any time, unless the personal information is necessary for us to fulfill our legal obligations. We will respect your decision, but may not be able to provide you with rental accommodation if we do not have sufficient personal information.

I/We, the undersigned (the 'Applicant', and if applicable the 'Co-Applicant') hereby apply for approval as a Tenant(s) of the Landlord based on the information provided on this page and the following pages. I/We understand that the information provided will be viewed to determine my/our suitability for the premises described below, and any other premises that the Landlord may deem appropriate.`;
};

const ACKNOWLEDGMENT_TEXT = `The Applicant and/or Co-Applicant acknowledges that pets, barbeques, waterbeds, pools and aquariums are not permitted without the advance written permission of the Landlord.

If the Landlord permits a pet, an additional Pet Damage Deposit between $250 - $300 will be paid to the Landlord. The Landlord will hold the Deposit(s) until the Tenancy ends.

The Applicant(s) do(es) hereby state that the information contained herein is true and correct, and contain no misrepresentations. If misrepresentations are found after a residential tenancy agreement is entered into with the Applicant and/or Co-Applicant, the Landlord shall have the option to terminate the residential tenancy agreement and seek all available remedies.

The Applicant(s) and/or Co-Applicant authorize the Landlord to obtain tenant history, credit, personal and employment information from one or more consumer reporting agencies, previous landlords, employers or previous employers and from other sources of such information, to verify the information provided by the Applicant[s] and/or Co-Applicant. The Applicant[s] and/or Co-Applicant authorize(s) the reporting agencies, and any other person, including personnel from any governmental ministry or agency, to disclose relevant information about the Applicant[s] and/or Co-Applicant to the Landlord. If the parties enter into a Residential tenancy agreement, the Applicant[s] and/or Co-Applicant authorize the above information to be used and disclosed for responding to emergencies, ensuring the orderly management of the tenancy and complying with legal requirements.`;

const PublicRentalApplicationForm = () => {
  const { propertyId: urlPropertyId, unitId: urlUnitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingSlug, propertyId: queryPropertyId } = parseBookingSearch(location.search);
  const scopePropertyId = urlPropertyId || queryPropertyId;
  
  const [linkProblem, setLinkProblem] = useState('');
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [managerName, setManagerName] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [expanded, setExpanded] = useState('section1');

  const [formData, setFormData] = useState({
    privacyPolicyAcknowledged: false,
    termsAccepted: false,
    propertyId: urlPropertyId || '',
    unitId: urlUnitId || '',
    dateOfOccupancyDesired: null,
    numberOfAdults: 1,
    numberOfChildren: 0,
    agesOfChildren: '',
    applicant: {
      name: '',
      currentAddress: '',
      email: '',
      phone1: '',
      phone2: '',
      dateOfBirth: null,
      sin: '',
      driversLicense: ''
    },
    coApplicant: {
      name: '',
      currentAddress: '',
      email: '',
      phone1: '',
      phone2: '',
      dateOfBirth: null,
      sin: '',
      driversLicense: ''
    },
    applicantPreviousTenancy: {
      landlordName: '',
      landlordPhone: '',
      previousAddress: '',
      timeAtLocation: '',
      monthlyRent: '',
      reasonForLeaving: ''
    },
    coApplicantPreviousTenancy: {
      landlordName: '',
      landlordPhone: '',
      previousAddress: '',
      timeAtLocation: '',
      monthlyRent: '',
      reasonForLeaving: ''
    },
    applicantEmployment: {
      status: '',
      employer: '',
      employmentAddress: '',
      supervisorName: '',
      supervisorPhone: '',
      lengthOfEmployment: '',
      monthlyIncome: '',
      jobPosition: '',
      otherIncomeSources: ''
    },
    coApplicantEmployment: {
      employer: '',
      employmentAddress: '',
      supervisorName: '',
      supervisorPhone: '',
      monthlyIncome: '',
      lengthOfEmployment: '',
      jobPosition: ''
    },
    applicantCreditHistory: {
      declaredBankruptcy: null,
      everEvicted: null,
      latePayments: null,
      refusedRent: null,
      explanation: ''
    },
    coApplicantCreditHistory: {
      declaredBankruptcy: null,
      everEvicted: null,
      latePayments: null,
      refusedRent: null,
      explanation: ''
    },
    additionalInfo: {
      hasPets: null,
      petDescription: '',
      smoking: null,
      creditReportConsent: null,
      hasInsurance: null
    },
    applicantReferences: [
      { name: '', relationship: '', phone: '' },
      { name: '', relationship: '', phone: '' }
    ],
    coApplicantReferences: [
      { name: '', relationship: '', phone: '' },
      { name: '', relationship: '', phone: '' }
    ],
    applicantEmergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    },
    coApplicantEmergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    },
    acknowledgmentAccepted: false
  });

  useEffect(() => {
    fetchPublicSettings();
    fetchProperties();
    loadDraft();
    
    // Auto-save every 30 seconds
    const autoSaveInterval = setInterval(() => {
      saveDraft();
    }, 30000);
    
    return () => clearInterval(autoSaveInterval);
  }, []);

  useEffect(() => {
    saveDraft();
  }, [formData]);

  const fetchPublicSettings = async () => {
    try {
      const response = await api.get('/api/settings/public');
      setManagerName(response.data.managerName || '');
      setJurisdiction(response.data.jurisdiction || '');
    } catch (error) {
      console.error('Error fetching public settings:', error);
    }
  };

  const fetchProperties = async () => {
    try {
      setLoading(true);
      // Only the landlord's link (?l=) or a property/unit link may list properties.
      if (!bookingSlug && !scopePropertyId) {
        setLinkProblem('missing');
        return;
      }
      const params = {};
      if (bookingSlug) params.l = bookingSlug;
      if (scopePropertyId) params.propertyId = scopePropertyId;

      let response;
      try {
        response = await api.get('/api/properties/public', { params });
      } catch (err) {
        if ([400, 404].includes(err.response?.status)) {
          setLinkProblem('invalid');
          return;
        }
        throw err;
      }

      let propertiesWithUnits = asArray(response.data).map((property) => ({
        ...property,
        units: Array.isArray(property.units) ? property.units : []
      }));
      if (scopePropertyId && propertiesWithUnits.some((p) => p.id === scopePropertyId)) {
        propertiesWithUnits = propertiesWithUnits.filter((p) => p.id === scopePropertyId);
      }
      setProperties(propertiesWithUnits);
      
      const allUnits = [];
      propertiesWithUnits.forEach(property => {
        if (property.units && Array.isArray(property.units) && property.units.length > 0) {
          property.units.forEach(unit => {
            // Handle both array of unit objects and array of unit IDs
            const unitId = unit.id || unit.unitId || (typeof unit === 'string' ? unit : null);
            const unitName = unit.name || unit.unitNumber || (typeof unit === 'object' ? `Unit ${unitId}` : unit);
            
            if (unitId) {
              allUnits.push({
                id: unitId,
                name: unitName,
                propertyId: property.id,
                propertyName: property.name || property.address || 'Unknown Property',
                fullLabel: `${property.name || property.address} - ${unitName}`
              });
            }
          });
        }
      });
      
      setUnits(allUnits);
      
      // Pre-select if URL params provided
      if (urlPropertyId && urlUnitId) {
        setFormData(prev => ({ ...prev, propertyId: urlPropertyId, unitId: urlUnitId }));
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load properties. Please refresh the page.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveDraft = () => {
    try {
      const draftData = { ...formData };
      if (draftData.applicant?.sin) {
        draftData.applicant = { ...draftData.applicant, sin: '' };
      }
      if (draftData.coApplicant?.sin) {
        draftData.coApplicant = { ...draftData.coApplicant, sin: '' };
      }
      localStorage.setItem('publicRentalApplicationDraft', JSON.stringify(draftData));
    } catch (error) {
      console.warn('Could not save draft:', error);
    }
  };

  const loadDraft = () => {
    try {
      const draft = localStorage.getItem('publicRentalApplicationDraft');
      if (draft) {
        const draftData = JSON.parse(draft);
        setFormData(draftData);
      }
    } catch (error) {
      console.warn('Could not load draft:', error);
    }
  };

  const updateField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateNestedField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateArrayField = (section, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const validateForm = () => {
    if (!formData.privacyPolicyAcknowledged || !formData.termsAccepted) {
      setSnackbar({ open: true, message: 'Please acknowledge privacy policy and terms', severity: 'error' });
      setExpanded('section1');
      return false;
    }
    if (!formData.propertyId || !formData.unitId || !formData.dateOfOccupancyDesired || !formData.numberOfAdults) {
      setSnackbar({ open: true, message: 'Please fill in all required property selection fields', severity: 'error' });
      setExpanded('section2');
      return false;
    }
    if (!formData.applicant.name || !formData.applicant.email || !formData.applicant.phone1 || !formData.applicant.dateOfBirth || !formData.applicant.currentAddress) {
      setSnackbar({ open: true, message: 'Please fill in all required applicant information', severity: 'error' });
      setExpanded('section3');
      return false;
    }
    if (formData.coApplicant.name && (!formData.coApplicant.email || !formData.coApplicant.phone1 || !formData.coApplicant.dateOfBirth)) {
      setSnackbar({ open: true, message: 'If co-applicant name is provided, email, phone, and date of birth are required', severity: 'error' });
      setExpanded('section4');
      return false;
    }
    const tenancy = formData.applicantPreviousTenancy;
    if (!tenancy.landlordName || !tenancy.landlordPhone || !tenancy.previousAddress || !tenancy.timeAtLocation || !tenancy.monthlyRent || !tenancy.reasonForLeaving) {
      setSnackbar({ open: true, message: 'Please fill in all previous tenancy information', severity: 'error' });
      setExpanded('section5');
      return false;
    }
    if (formData.coApplicant.name) {
      const coTenancy = formData.coApplicantPreviousTenancy;
      if (!coTenancy.landlordName || !coTenancy.landlordPhone || !coTenancy.previousAddress || !coTenancy.timeAtLocation || !coTenancy.monthlyRent || !coTenancy.reasonForLeaving) {
        setSnackbar({ open: true, message: 'Please fill in all co-applicant previous tenancy information', severity: 'error' });
        setExpanded('section6');
        return false;
      }
    }
    const emp = formData.applicantEmployment;
    if (!emp.status || !emp.employer || !emp.employmentAddress || !emp.supervisorName || !emp.supervisorPhone || !emp.lengthOfEmployment || !emp.monthlyIncome || !emp.jobPosition) {
      setSnackbar({ open: true, message: 'Please fill in all employment information', severity: 'error' });
      setExpanded('section7');
      return false;
    }
    if (formData.coApplicant.name) {
      const coEmp = formData.coApplicantEmployment;
      if (!coEmp.employer || !coEmp.employmentAddress || !coEmp.supervisorName || !coEmp.supervisorPhone || !coEmp.monthlyIncome || !coEmp.lengthOfEmployment || !coEmp.jobPosition) {
        setSnackbar({ open: true, message: 'Please fill in all co-applicant employment information', severity: 'error' });
        setExpanded('section8');
        return false;
      }
    }
    const credit = formData.applicantCreditHistory;
    if (credit.declaredBankruptcy === null || credit.everEvicted === null || credit.latePayments === null || credit.refusedRent === null) {
      setSnackbar({ open: true, message: 'Please answer all credit history questions', severity: 'error' });
      setExpanded('section9');
      return false;
    }
    if ((credit.declaredBankruptcy === true || credit.everEvicted === true || credit.latePayments === true || credit.refusedRent === true) && !credit.explanation) {
      setSnackbar({ open: true, message: 'Please provide an explanation for any "Yes" answers', severity: 'error' });
      setExpanded('section9');
      return false;
    }
    if (formData.coApplicant.name) {
      const coCredit = formData.coApplicantCreditHistory;
      if (coCredit.declaredBankruptcy === null || coCredit.everEvicted === null || coCredit.latePayments === null || coCredit.refusedRent === null) {
        setSnackbar({ open: true, message: 'Please answer all co-applicant credit history questions', severity: 'error' });
        setExpanded('section10');
        return false;
      }
      if ((coCredit.declaredBankruptcy === true || coCredit.everEvicted === true || coCredit.latePayments === true || coCredit.refusedRent === true) && !coCredit.explanation) {
        setSnackbar({ open: true, message: 'Please provide an explanation for any "Yes" answers', severity: 'error' });
        setExpanded('section10');
        return false;
      }
    }
    const addInfo = formData.additionalInfo;
    if (addInfo.hasPets === null || addInfo.smoking === null || addInfo.creditReportConsent === null || addInfo.hasInsurance === null) {
      setSnackbar({ open: true, message: 'Please answer all additional information questions', severity: 'error' });
      setExpanded('section11');
      return false;
    }
    if (addInfo.hasPets === true && !addInfo.petDescription) {
      setSnackbar({ open: true, message: 'Please describe your pets', severity: 'error' });
      setExpanded('section11');
      return false;
    }
    if (!formData.applicantReferences[0].name || !formData.applicantReferences[0].relationship || !formData.applicantReferences[0].phone) {
      setSnackbar({ open: true, message: 'Please provide at least one reference', severity: 'error' });
      setExpanded('section12');
      return false;
    }
    if (formData.coApplicant.name) {
      if (!formData.coApplicantReferences[0].name || !formData.coApplicantReferences[0].relationship || !formData.coApplicantReferences[0].phone) {
        setSnackbar({ open: true, message: 'Please provide at least one co-applicant reference', severity: 'error' });
        setExpanded('section13');
        return false;
      }
    }
    const emergency = formData.applicantEmergencyContact;
    if (!emergency.name || !emergency.phone || !emergency.relationship) {
      setSnackbar({ open: true, message: 'Please provide emergency contact information', severity: 'error' });
      setExpanded('section14');
      return false;
    }
    if (formData.coApplicant.name) {
      const coEmergency = formData.coApplicantEmergencyContact;
      if (!coEmergency.name || !coEmergency.phone || !coEmergency.relationship) {
        setSnackbar({ open: true, message: 'Please provide co-applicant emergency contact information', severity: 'error' });
        setExpanded('section15');
        return false;
      }
    }
    if (!formData.acknowledgmentAccepted) {
      setSnackbar({ open: true, message: 'Please accept the acknowledgment terms', severity: 'error' });
      setExpanded('section16');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const submissionData = {
        ...formData,
        propertyId: formData.propertyId,
        unitId: formData.unitId,
        dateOfOccupancyDesired: formData.dateOfOccupancyDesired || null,
        applicant: {
          ...formData.applicant,
          dateOfBirth: formData.applicant.dateOfBirth || null
        },
        coApplicant: formData.coApplicant.name ? {
          ...formData.coApplicant,
          dateOfBirth: formData.coApplicant.dateOfBirth || null
        } : null,
        coApplicantPreviousTenancy: formData.coApplicant.name ? formData.coApplicantPreviousTenancy : null,
        coApplicantEmployment: formData.coApplicant.name ? formData.coApplicantEmployment : null,
        coApplicantCreditHistory: formData.coApplicant.name ? formData.coApplicantCreditHistory : null,
        coApplicantReferences: formData.coApplicant.name ? formData.coApplicantReferences : null,
        coApplicantEmergencyContact: formData.coApplicant.name ? formData.coApplicantEmergencyContact : null
      };

      const response = await api.post('/api/applications/submit', submissionData);
      
      localStorage.removeItem('publicRentalApplicationDraft');
      
      setSnackbar({
        open: true,
        message: `Application submitted successfully! Reference: ${response.data.applicationReference}`,
        severity: 'success'
      });

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/application-success');
      }, 3000);
    } catch (error) {
      console.error('Error submitting application:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to submit application. Please try again.',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>Loading application form...</Typography>
      </Container>
    );
  }

  if (linkProblem) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 6 } }}>
        <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            {linkProblem === 'invalid' ? 'This application link is no longer valid' : 'Application link needed'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please ask the property manager for their rental application link. It opens this form
            with their properties ready to choose from.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom align="center">
        Rental Application
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
        Please complete all sections below. Your progress is automatically saved.
      </Typography>

      {/* Section 1: Privacy Policy & Consent */}
      <Accordion expanded={expanded === 'section1'} onChange={handleAccordionChange('section1')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">1. Privacy Policy & Consent</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ p: 2, mb: 3, maxHeight: 300, overflow: 'auto' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {getPrivacyPolicyText(managerName, jurisdiction)}
            </Typography>
          </Paper>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.privacyPolicyAcknowledged}
                onChange={(e) => setFormData(prev => ({ ...prev, privacyPolicyAcknowledged: e.target.checked }))}
              />
            }
            label="I acknowledge the privacy policy above *"
          />
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.termsAccepted}
                  onChange={(e) => setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                />
              }
              label="I accept the terms and conditions *"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Section 2: Property Selection */}
      <Accordion expanded={expanded === 'section2'} onChange={handleAccordionChange('section2')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">2. Property Selection</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Select Property & Unit</InputLabel>
                <Select
                  value={formData.propertyId && formData.unitId ? `${formData.propertyId}|${formData.unitId}` : ''}
                  label="Select Property & Unit"
                  onChange={(e) => {
                    const [propertyId, unitId] = e.target.value.split('|');
                    setFormData(prev => ({ ...prev, propertyId, unitId }));
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <MenuItem disabled>Loading properties...</MenuItem>
                  ) : units.length === 0 ? (
                    <MenuItem disabled>
                      {properties.length === 0 
                        ? 'No properties available. Please contact the property manager.' 
                        : 'No units available for the selected properties.'}
                    </MenuItem>
                  ) : (
                    units.map(unit => (
                      <MenuItem key={`${unit.propertyId}|${unit.id}`} value={`${unit.propertyId}|${unit.id}`}>
                        {unit.fullLabel}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date of Occupancy Desired *"
                value={formData.dateOfOccupancyDesired || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, dateOfOccupancyDesired: e.target.value || null }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Number of Adults *"
                value={formData.numberOfAdults}
                onChange={(e) => setFormData(prev => ({ ...prev, numberOfAdults: parseInt(e.target.value) || 1 }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Number of Children"
                value={formData.numberOfChildren}
                onChange={(e) => setFormData(prev => ({ ...prev, numberOfChildren: parseInt(e.target.value) || 0 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Age(s) of Children"
                value={formData.agesOfChildren}
                onChange={(e) => setFormData(prev => ({ ...prev, agesOfChildren: e.target.value }))}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Section 3: Applicant Personal Information */}
      <Accordion expanded={expanded === 'section3'} onChange={handleAccordionChange('section3')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">3. Applicant Personal Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name *" value={formData.applicant.name} onChange={(e) => updateField('applicant', 'name', e.target.value)} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Current Address *" value={formData.applicant.currentAddress} onChange={(e) => updateField('applicant', 'currentAddress', e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="email" label="Email *" value={formData.applicant.email} onChange={(e) => updateField('applicant', 'email', e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="tel" label="Phone 1 *" value={formData.applicant.phone1} onChange={(e) => updateField('applicant', 'phone1', e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="tel" label="Phone 2" value={formData.applicant.phone2} onChange={(e) => updateField('applicant', 'phone2', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date of Birth *"
                value={formData.applicant.dateOfBirth || ''}
                onChange={(e) => updateField('applicant', 'dateOfBirth', e.target.value || null)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="SIN/SSN (optional, for credit checks)" type="password" value={formData.applicant.sin} onChange={(e) => updateField('applicant', 'sin', e.target.value)} helperText="Will be encrypted and stored securely" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Driver's License Number (optional)" value={formData.applicant.driversLicense} onChange={(e) => updateField('applicant', 'driversLicense', e.target.value)} />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Section 4: Co-Applicant Personal Information */}
      <Accordion expanded={expanded === 'section4'} onChange={handleAccordionChange('section4')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">4. Co-Applicant Personal Information (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Co-Applicant Name" value={formData.coApplicant.name} onChange={(e) => updateField('coApplicant', 'name', e.target.value)} helperText="If you have a co-applicant, fill in their information" />
            </Grid>
            {formData.coApplicant.name && (
              <>
                <Grid item xs={12}>
                  <TextField fullWidth label="Co-Applicant Current Address" value={formData.coApplicant.currentAddress} onChange={(e) => updateField('coApplicant', 'currentAddress', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="email" label="Co-Applicant Email" value={formData.coApplicant.email} onChange={(e) => updateField('coApplicant', 'email', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="tel" label="Co-Applicant Phone 1" value={formData.coApplicant.phone1} onChange={(e) => updateField('coApplicant', 'phone1', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="tel" label="Co-Applicant Phone 2" value={formData.coApplicant.phone2} onChange={(e) => updateField('coApplicant', 'phone2', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Co-Applicant Date of Birth"
                    value={formData.coApplicant.dateOfBirth || ''}
                    onChange={(e) => updateField('coApplicant', 'dateOfBirth', e.target.value || null)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Co-Applicant SIN/SSN (optional)" type="password" value={formData.coApplicant.sin} onChange={(e) => updateField('coApplicant', 'sin', e.target.value)} helperText="Will be encrypted and stored securely" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Co-Applicant Driver's License Number (optional)" value={formData.coApplicant.driversLicense} onChange={(e) => updateField('coApplicant', 'driversLicense', e.target.value)} />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Section 5: Previous Tenancy - Applicant */}
      <Accordion expanded={expanded === 'section5'} onChange={handleAccordionChange('section5')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">5. Previous Tenancy - Applicant</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Landlord's Name *" value={formData.applicantPreviousTenancy.landlordName} onChange={(e) => updateNestedField('applicantPreviousTenancy', 'landlordName', e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="tel" label="Landlord's Phone *" value={formData.applicantPreviousTenancy.landlordPhone} onChange={(e) => updateNestedField('applicantPreviousTenancy', 'landlordPhone', e.target.value)} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Previous Address *" value={formData.applicantPreviousTenancy.previousAddress} onChange={(e) => updateNestedField('applicantPreviousTenancy', 'previousAddress', e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Time at this location *" value={formData.applicantPreviousTenancy.timeAtLocation} onChange={(e) => updateNestedField('applicantPreviousTenancy', 'timeAtLocation', e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Monthly Rent *" value={formData.applicantPreviousTenancy.monthlyRent} onChange={(e) => updateNestedField('applicantPreviousTenancy', 'monthlyRent', e.target.value)} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Reason for Leaving *" value={formData.applicantPreviousTenancy.reasonForLeaving} onChange={(e) => updateNestedField('applicantPreviousTenancy', 'reasonForLeaving', e.target.value)} required />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Section 6: Previous Tenancy - Co-Applicant */}
      {formData.coApplicant.name && (
        <Accordion expanded={expanded === 'section6'} onChange={handleAccordionChange('section6')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">6. Previous Tenancy - Co-Applicant</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Co-Applicant Landlord's Name *" value={formData.coApplicantPreviousTenancy.landlordName} onChange={(e) => updateNestedField('coApplicantPreviousTenancy', 'landlordName', e.target.value)} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="tel" label="Co-Applicant Landlord's Phone *" value={formData.coApplicantPreviousTenancy.landlordPhone} onChange={(e) => updateNestedField('coApplicantPreviousTenancy', 'landlordPhone', e.target.value)} required />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Co-Applicant Previous Address *" value={formData.coApplicantPreviousTenancy.previousAddress} onChange={(e) => updateNestedField('coApplicantPreviousTenancy', 'previousAddress', e.target.value)} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Co-Applicant Time at this location *" value={formData.coApplicantPreviousTenancy.timeAtLocation} onChange={(e) => updateNestedField('coApplicantPreviousTenancy', 'timeAtLocation', e.target.value)} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="number" label="Co-Applicant Monthly Rent *" value={formData.coApplicantPreviousTenancy.monthlyRent} onChange={(e) => updateNestedField('coApplicantPreviousTenancy', 'monthlyRent', e.target.value)} required />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="Co-Applicant Reason for Leaving *" value={formData.coApplicantPreviousTenancy.reasonForLeaving} onChange={(e) => updateNestedField('coApplicantPreviousTenancy', 'reasonForLeaving', e.target.value)} required />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 7: Employment Information - Applicant */}
      <Accordion expanded={expanded === 'section7'} onChange={handleAccordionChange('section7')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">7. Employment Information - Applicant</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Status *</InputLabel>
                <Select value={formData.applicantEmployment.status} label="Status *" onChange={(e) => updateField('applicantEmployment', 'status', e.target.value)}>
                  <MenuItem value="Full Time">Full Time</MenuItem>
                  <MenuItem value="Part Time">Part Time</MenuItem>
                  <MenuItem value="Student">Student</MenuItem>
                  <MenuItem value="Unemployed">Unemployed</MenuItem>
                  <MenuItem value="Retired">Retired</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Current Employer *" value={formData.applicantEmployment.employer} onChange={(e) => updateField('applicantEmployment', 'employer', e.target.value)} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Employment Address *" value={formData.applicantEmployment.employmentAddress} onChange={(e) => updateField('applicantEmployment', 'employmentAddress', e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Supervisor Name *" value={formData.applicantEmployment.supervisorName} onChange={(e) => updateField('applicantEmployment', 'supervisorName', e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="tel" label="Supervisor Phone *" value={formData.applicantEmployment.supervisorPhone} onChange={(e) => updateField('applicantEmployment', 'supervisorPhone', e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Length of Employment *" value={formData.applicantEmployment.lengthOfEmployment} onChange={(e) => updateField('applicantEmployment', 'lengthOfEmployment', e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Monthly Income *" value={formData.applicantEmployment.monthlyIncome} onChange={(e) => updateField('applicantEmployment', 'monthlyIncome', e.target.value)} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Job Position *" value={formData.applicantEmployment.jobPosition} onChange={(e) => updateField('applicantEmployment', 'jobPosition', e.target.value)} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Other sources of income (if any)" value={formData.applicantEmployment.otherIncomeSources} onChange={(e) => updateField('applicantEmployment', 'otherIncomeSources', e.target.value)} />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Section 8: Co-Applicant Employment */}
      {formData.coApplicant.name && (
        <Accordion expanded={expanded === 'section8'} onChange={handleAccordionChange('section8')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">8. Co-Applicant Employment Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Co-Applicant Employer" value={formData.coApplicantEmployment.employer} onChange={(e) => updateField('coApplicantEmployment', 'employer', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Co-Applicant Employment Address" value={formData.coApplicantEmployment.employmentAddress} onChange={(e) => updateField('coApplicantEmployment', 'employmentAddress', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Co-Applicant Supervisor Name" value={formData.coApplicantEmployment.supervisorName} onChange={(e) => updateField('coApplicantEmployment', 'supervisorName', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="tel" label="Co-Applicant Supervisor Phone" value={formData.coApplicantEmployment.supervisorPhone} onChange={(e) => updateField('coApplicantEmployment', 'supervisorPhone', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Co-Applicant Length of Employment" value={formData.coApplicantEmployment.lengthOfEmployment} onChange={(e) => updateField('coApplicantEmployment', 'lengthOfEmployment', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="number" label="Co-Applicant Monthly Income" value={formData.coApplicantEmployment.monthlyIncome} onChange={(e) => updateField('coApplicantEmployment', 'monthlyIncome', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Co-Applicant Job Position" value={formData.coApplicantEmployment.jobPosition} onChange={(e) => updateField('coApplicantEmployment', 'jobPosition', e.target.value)} />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 9: Credit History - Applicant */}
      <Accordion expanded={expanded === 'section9'} onChange={handleAccordionChange('section9')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">9. Credit History & Rental Background - Applicant</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component="fieldset" required>
                <FormLabel component="legend">Have you declared bankruptcy in the past seven (7) years? *</FormLabel>
                <RadioGroup row value={formData.applicantCreditHistory.declaredBankruptcy === true ? 'true' : formData.applicantCreditHistory.declaredBankruptcy === false ? 'false' : ''} onChange={(e) => updateNestedField('applicantCreditHistory', 'declaredBankruptcy', e.target.value === 'true')}>
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" required>
                <FormLabel component="legend">Have you ever been evicted from a rental residence? *</FormLabel>
                <RadioGroup row value={formData.applicantCreditHistory.everEvicted === true ? 'true' : formData.applicantCreditHistory.everEvicted === false ? 'false' : ''} onChange={(e) => updateNestedField('applicantCreditHistory', 'everEvicted', e.target.value === 'true')}>
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" required>
                <FormLabel component="legend">Have you had two or more late rental payments in the past 12 months? *</FormLabel>
                <RadioGroup row value={formData.applicantCreditHistory.latePayments === true ? 'true' : formData.applicantCreditHistory.latePayments === false ? 'false' : ''} onChange={(e) => updateNestedField('applicantCreditHistory', 'latePayments', e.target.value === 'true')}>
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" required>
                <FormLabel component="legend">Have you ever refused to pay rent when due? *</FormLabel>
                <RadioGroup row value={formData.applicantCreditHistory.refusedRent === true ? 'true' : formData.applicantCreditHistory.refusedRent === false ? 'false' : ''} onChange={(e) => updateNestedField('applicantCreditHistory', 'refusedRent', e.target.value === 'true')}>
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {(formData.applicantCreditHistory.declaredBankruptcy === true || 
              formData.applicantCreditHistory.everEvicted === true || 
              formData.applicantCreditHistory.latePayments === true || 
              formData.applicantCreditHistory.refusedRent === true) && (
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={4} label="If you answered YES to any of the above, please state your reasons and/or circumstances *" value={formData.applicantCreditHistory.explanation} onChange={(e) => updateNestedField('applicantCreditHistory', 'explanation', e.target.value)} required />
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Section 10: Credit History - Co-Applicant */}
      {formData.coApplicant.name && (
        <Accordion expanded={expanded === 'section10'} onChange={handleAccordionChange('section10')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">10. Credit History & Rental Background - Co-Applicant</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl component="fieldset" required>
                  <FormLabel component="legend">Has co-applicant declared bankruptcy in the past seven (7) years? *</FormLabel>
                  <RadioGroup row value={formData.coApplicantCreditHistory.declaredBankruptcy === true ? 'true' : formData.coApplicantCreditHistory.declaredBankruptcy === false ? 'false' : ''} onChange={(e) => updateNestedField('coApplicantCreditHistory', 'declaredBankruptcy', e.target.value === 'true')}>
                    <FormControlLabel value="true" control={<Radio />} label="Yes" />
                    <FormControlLabel value="false" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" required>
                  <FormLabel component="legend">Has co-applicant ever been evicted? *</FormLabel>
                  <RadioGroup row value={formData.coApplicantCreditHistory.everEvicted === true ? 'true' : formData.coApplicantCreditHistory.everEvicted === false ? 'false' : ''} onChange={(e) => updateNestedField('coApplicantCreditHistory', 'everEvicted', e.target.value === 'true')}>
                    <FormControlLabel value="true" control={<Radio />} label="Yes" />
                    <FormControlLabel value="false" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" required>
                  <FormLabel component="legend">Has co-applicant had two or more late payments in the past 12 months? *</FormLabel>
                  <RadioGroup row value={formData.coApplicantCreditHistory.latePayments === true ? 'true' : formData.coApplicantCreditHistory.latePayments === false ? 'false' : ''} onChange={(e) => updateNestedField('coApplicantCreditHistory', 'latePayments', e.target.value === 'true')}>
                    <FormControlLabel value="true" control={<Radio />} label="Yes" />
                    <FormControlLabel value="false" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" required>
                  <FormLabel component="legend">Has co-applicant ever refused to pay rent? *</FormLabel>
                  <RadioGroup row value={formData.coApplicantCreditHistory.refusedRent === true ? 'true' : formData.coApplicantCreditHistory.refusedRent === false ? 'false' : ''} onChange={(e) => updateNestedField('coApplicantCreditHistory', 'refusedRent', e.target.value === 'true')}>
                    <FormControlLabel value="true" control={<Radio />} label="Yes" />
                    <FormControlLabel value="false" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              {(formData.coApplicantCreditHistory.declaredBankruptcy === true || 
                formData.coApplicantCreditHistory.everEvicted === true || 
                formData.coApplicantCreditHistory.latePayments === true || 
                formData.coApplicantCreditHistory.refusedRent === true) && (
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={4} label="If co-applicant answered YES to any of the above, please state reasons and/or circumstances *" value={formData.coApplicantCreditHistory.explanation} onChange={(e) => updateNestedField('coApplicantCreditHistory', 'explanation', e.target.value)} required />
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 11: Additional Information */}
      <Accordion expanded={expanded === 'section11'} onChange={handleAccordionChange('section11')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">11. Additional Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component="fieldset" required>
                <FormLabel component="legend">Do you wish to bring a pet(s) to the rental premises? *</FormLabel>
                <RadioGroup row value={formData.additionalInfo.hasPets === true ? 'true' : formData.additionalInfo.hasPets === false ? 'false' : ''} onChange={(e) => updateField('additionalInfo', 'hasPets', e.target.value === 'true')}>
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {formData.additionalInfo.hasPets === true && (
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="If yes, describe the pets *" value={formData.additionalInfo.petDescription} onChange={(e) => updateField('additionalInfo', 'petDescription', e.target.value)} required />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl component="fieldset" required>
                <FormLabel component="legend">Do you, or any proposed occupant, smoke? *</FormLabel>
                <RadioGroup row value={formData.additionalInfo.smoking === true ? 'true' : formData.additionalInfo.smoking === false ? 'false' : ''} onChange={(e) => updateField('additionalInfo', 'smoking', e.target.value === 'true')}>
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" required>
                <FormLabel component="legend">Do you consent to a credit report? A separate email will be sent with a link to a secure online consent form for your e-signature. *</FormLabel>
                <RadioGroup row value={formData.additionalInfo.creditReportConsent === true ? 'true' : formData.additionalInfo.creditReportConsent === false ? 'false' : ''} onChange={(e) => updateField('additionalInfo', 'creditReportConsent', e.target.value === 'true')}>
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" required>
                <FormLabel component="legend">I/We presently insure our belongings and for third party liability: *</FormLabel>
                <RadioGroup row value={formData.additionalInfo.hasInsurance === true ? 'true' : formData.additionalInfo.hasInsurance === false ? 'false' : ''} onChange={(e) => updateField('additionalInfo', 'hasInsurance', e.target.value === 'true')}>
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Section 12: References - Applicant */}
      <Accordion expanded={expanded === 'section12'} onChange={handleAccordionChange('section12')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">12. References - Applicant</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Reference 1 *</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Name *" value={formData.applicantReferences[0].name} onChange={(e) => updateArrayField('applicantReferences', 0, 'name', e.target.value)} required />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Relationship *" value={formData.applicantReferences[0].relationship} onChange={(e) => updateArrayField('applicantReferences', 0, 'relationship', e.target.value)} required />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="tel" label="Phone *" value={formData.applicantReferences[0].phone} onChange={(e) => updateArrayField('applicantReferences', 0, 'phone', e.target.value)} required />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Reference 2 (Optional)</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Name" value={formData.applicantReferences[1].name} onChange={(e) => updateArrayField('applicantReferences', 1, 'name', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Relationship" value={formData.applicantReferences[1].relationship} onChange={(e) => updateArrayField('applicantReferences', 1, 'relationship', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="tel" label="Phone" value={formData.applicantReferences[1].phone} onChange={(e) => updateArrayField('applicantReferences', 1, 'phone', e.target.value)} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Section 13: References - Co-Applicant */}
      {formData.coApplicant.name && (
        <Accordion expanded={expanded === 'section13'} onChange={handleAccordionChange('section13')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">13. References - Co-Applicant</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Co-Applicant Reference 1 *</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Name *" value={formData.coApplicantReferences[0].name} onChange={(e) => updateArrayField('coApplicantReferences', 0, 'name', e.target.value)} required />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Relationship *" value={formData.coApplicantReferences[0].relationship} onChange={(e) => updateArrayField('coApplicantReferences', 0, 'relationship', e.target.value)} required />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth type="tel" label="Phone *" value={formData.coApplicantReferences[0].phone} onChange={(e) => updateArrayField('coApplicantReferences', 0, 'phone', e.target.value)} required />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Co-Applicant Reference 2 (Optional)</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Name" value={formData.coApplicantReferences[1].name} onChange={(e) => updateArrayField('coApplicantReferences', 1, 'name', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Relationship" value={formData.coApplicantReferences[1].relationship} onChange={(e) => updateArrayField('coApplicantReferences', 1, 'relationship', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth type="tel" label="Phone" value={formData.coApplicantReferences[1].phone} onChange={(e) => updateArrayField('coApplicantReferences', 1, 'phone', e.target.value)} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 14: Emergency Contact - Applicant */}
      <Accordion expanded={expanded === 'section14'} onChange={handleAccordionChange('section14')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">14. Emergency Contact - Applicant</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Name of a person not residing with you
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Name *" value={formData.applicantEmergencyContact.name} onChange={(e) => updateField('applicantEmergencyContact', 'name', e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="tel" label="Phone *" value={formData.applicantEmergencyContact.phone} onChange={(e) => updateField('applicantEmergencyContact', 'phone', e.target.value)} required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Relationship *" value={formData.applicantEmergencyContact.relationship} onChange={(e) => updateField('applicantEmergencyContact', 'relationship', e.target.value)} required />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Section 15: Emergency Contact - Co-Applicant */}
      {formData.coApplicant.name && (
        <Accordion expanded={expanded === 'section15'} onChange={handleAccordionChange('section15')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">15. Emergency Contact - Co-Applicant</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Name of a person not residing with co-applicant
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Name *" value={formData.coApplicantEmergencyContact.name} onChange={(e) => updateField('coApplicantEmergencyContact', 'name', e.target.value)} required />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth type="tel" label="Phone *" value={formData.coApplicantEmergencyContact.phone} onChange={(e) => updateField('coApplicantEmergencyContact', 'phone', e.target.value)} required />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Relationship *" value={formData.coApplicantEmergencyContact.relationship} onChange={(e) => updateField('coApplicantEmergencyContact', 'relationship', e.target.value)} required />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 16: Acknowledgment */}
      <Accordion expanded={expanded === 'section16'} onChange={handleAccordionChange('section16')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">16. Acknowledgment</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ p: 2, mb: 3, maxHeight: 300, overflow: 'auto' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {ACKNOWLEDGMENT_TEXT}
            </Typography>
          </Paper>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.acknowledgmentAccepted}
                onChange={(e) => setFormData(prev => ({ ...prev, acknowledgmentAccepted: e.target.checked }))}
              />
            }
            label="Do you acknowledge and understand these terms? *"
          />
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            sx={{ minWidth: 200 }}
          >
            {submitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PublicRentalApplicationForm;






